import { useState, useEffect, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  Layers,
  FileSpreadsheet,
  RotateCcw,
  CheckCircle2,
  AlertTriangle,
  Download,
  Upload,
  Search,
  ExternalLink,
  History,
  FileCheck,
  Check,
  X,
  RefreshCw,
  Eye,
} from 'lucide-react'
import * as XLSX from 'xlsx'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { PageError, PageLoading } from '@/components/ui/PageAsyncState'
import {
  dataStandardizationService,
  type DataScanType,
  type DataProposalRow,
  type DataScanStats,
  type DataChangeBatch,
  type DataChangeLine,
  type HardwareImportRow,
  type HardwareImportPreviewResult,
  type ConfidenceLevel,
} from '@/services/dataStandardization'
import { useCan } from '@/features/auth/AuthContext'
import { formatDateFull } from '@/lib/dateFormat'

type TabKey = 'materials' | 'duplicate_specs' | 'standard_packaging' | 'structured_packaging' | 'import_hardware' | 'bom_names' | 'history'

export function DataStandardizationHubPage() {
  const canWriteCatalog = useCan('catalog.write')
  const canAdminUsers = useCan('admin.users')
  const canWriteBom = useCan('bom.write')
  const canWrite = canWriteCatalog || canAdminUsers || canWriteBom
  const [activeTab, setActiveTab] = useState<TabKey>('materials')

  // Scan state
  const [loadingScan, setLoadingScan] = useState(false)
  const [scanError, setScanError] = useState<string | null>(null)
  const [proposals, setProposals] = useState<DataProposalRow[]>([])
  const [stats, setStats] = useState<DataScanStats | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Filter state
  const [confidenceFilter, setConfidenceFilter] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')

  // Apply Modal state
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false)
  const [applyBatchName, setApplyBatchName] = useState('')
  const [applyNotes, setApplyNotes] = useState('')
  const [applying, setApplying] = useState(false)
  const [actionSuccessMessage, setActionSuccessMessage] = useState<string | null>(null)

  // Hardware import state
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [importingFile, setImportingFile] = useState(false)
  const [importPreview, setImportPreview] = useState<HardwareImportPreviewResult | null>(null)
  const [applyingImport, setApplyingImport] = useState(false)

  // History state
  const [batches, setBatches] = useState<DataChangeBatch[]>([])
  const [loadingBatches, setLoadingBatches] = useState(false)
  const [revertingBatchId, setRevertingBatchId] = useState<string | null>(null)

  // Detail Lines Modal state
  const [selectedBatchForDetail, setSelectedBatchForDetail] = useState<DataChangeBatch | null>(null)
  const [detailLines, setDetailLines] = useState<DataChangeLine[]>([])
  const [loadingDetailLines, setLoadingDetailLines] = useState(false)

  // Undo Confirm Modal state
  const [batchToUndo, setBatchToUndo] = useState<DataChangeBatch | null>(null)

  // Load scan based on active tab
  async function triggerScan(type: DataScanType) {
    setLoadingScan(true)
    setScanError(null)
    try {
      const res = await dataStandardizationService.scan(type)
      setProposals(res.proposals)
      setStats(res.stats)

      // Pre-select CONFIDENT items only
      const preSelected = new Set<string>()
      for (const p of res.proposals) {
        if (p.confidence === 'CONFIDENT') {
          preSelected.add(p.id)
        }
      }
      setSelectedIds(preSelected)
    } catch (err: any) {
      setScanError(err.message || 'Lỗi khi quét dữ liệu')
    } finally {
      setLoadingScan(false)
    }
  }

  // Load batches history
  async function loadBatches() {
    setLoadingBatches(true)
    try {
      const list = await dataStandardizationService.getBatches()
      setBatches(list)
    } catch {
      // Best effort load history
    } finally {
      setLoadingBatches(false)
    }
  }

  // Effect when switching tabs
  useEffect(() => {
    setActionSuccessMessage(null)
    if (activeTab === 'materials') {
      void triggerScan('MATERIALS')
    } else if (activeTab === 'duplicate_specs') {
      void triggerScan('DUPLICATE_SPECS')
    } else if (activeTab === 'standard_packaging') {
      void triggerScan('STANDARD_PACKAGING')
    } else if (activeTab === 'structured_packaging') {
      void triggerScan('STRUCTURED_PACKAGING')
    } else if (activeTab === 'history') {
      void loadBatches()
    }
  }, [activeTab])

  // Initial load history
  useEffect(() => {
    void loadBatches()
  }, [])

  // Filtered proposals
  const filteredProposals = useMemo(() => {
    return proposals.filter((p) => {
      if (confidenceFilter && p.confidence !== confidenceFilter) return false
      if (searchTerm) {
        const term = searchTerm.toLowerCase()
        const matchCode = p.entity_code.toLowerCase().includes(term)
        const matchName = p.entity_name.toLowerCase().includes(term)
        const matchReason = p.reason.toLowerCase().includes(term)
        const matchCurrent = p.current_display.toLowerCase().includes(term)
        const matchProposed = p.proposed_display.toLowerCase().includes(term)
        if (!matchCode && !matchName && !matchReason && !matchCurrent && !matchProposed) return false
      }
      return true
    })
  }, [proposals, confidenceFilter, searchTerm])

  const selectedList = useMemo(() => {
    return proposals.filter((p) => selectedIds.has(p.id))
  }, [proposals, selectedIds])

  function toggleSelectRow(id: string) {
    const next = new Set(selectedIds)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setSelectedIds(next)
  }

  function handleSelectAll(select: boolean) {
    const next = new Set(selectedIds)
    for (const p of filteredProposals) {
      if (select) {
        next.add(p.id)
      } else {
        next.delete(p.id)
      }
    }
    setSelectedIds(next)
  }

  function handleSelectConfidentOnly() {
    const next = new Set<string>()
    for (const p of proposals) {
      if (p.confidence === 'CONFIDENT') {
        next.add(p.id)
      }
    }
    setSelectedIds(next)
  }

  // Export proposals to Excel
  function exportProposalsExcel() {
    const rows = filteredProposals.map((p, idx) => ({
      STT: idx + 1,
      'Bộ quy tắc': p.scan_type,
      'Loại đối tượng': p.entity_type,
      'Mã đối tượng': p.entity_code,
      'Tên đối tượng': p.entity_name,
      'Hành động': p.action,
      'Trường thay đổi': p.field,
      'Hiện tại': p.current_display,
      'Đề xuất': p.proposed_display,
      'Mức tin cậy':
        p.confidence === 'CONFIDENT'
          ? 'Chắc chắn'
          : p.confidence === 'NEEDS_REVIEW'
            ? 'Cần xem'
            : 'Không rõ',
      'Lý do': p.reason,
      'Tác động': p.impact,
    }))

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(rows)
    XLSX.utils.book_append_sheet(wb, ws, 'De_xuat_chuan_hoa')
    XLSX.writeFile(wb, `De_xuat_chuan_hoa_${activeTab}_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  // Confirm Apply
  async function handleConfirmApply() {
    if (selectedList.length === 0) return
    setApplying(true)
    try {
      const scanType =
        activeTab === 'materials'
          ? 'MATERIALS'
          : activeTab === 'duplicate_specs'
            ? 'DUPLICATE_SPECS'
            : activeTab === 'standard_packaging'
              ? 'STANDARD_PACKAGING'
              : 'STRUCTURED_PACKAGING'

      const res = await dataStandardizationService.applyBatch({
        batch_name:
          applyBatchName.trim() ||
          `Chuẩn hóa ${scanType} (${selectedList.length} dòng - ${formatDateFull(new Date())})`,
        scan_type: scanType,
        proposals: selectedList,
      })

      setIsApplyModalOpen(false)
      setActionSuccessMessage(
        `Áp dụng thành công lô '${res.batch.batch_name}': Đã chuẩn hóa ${res.applied_count} dòng.${
          res.skipped_count > 0 ? ` (Bỏ qua ${res.skipped_count} dòng do dữ liệu đã thay đổi)` : ''
        }`,
      )

      // Refresh current scan and history
      await triggerScan(scanType)
      await loadBatches()
    } catch (err: any) {
      alert(err.message || 'Lỗi khi áp dụng lô thay đổi')
    } finally {
      setApplying(false)
    }
  }

  // Confirm Undo
  async function handleConfirmUndo() {
    if (!batchToUndo) return
    const batchId = batchToUndo.id
    setRevertingBatchId(batchId)
    try {
      const res = await dataStandardizationService.undoBatch(batchId)
      setBatchToUndo(null)
      setActionSuccessMessage(
        `Hoàn tác thành công lô '${res.batch.batch_name}': Đã khôi phục ${res.reverted_count} dòng.${
          res.skipped_count > 0
            ? ` (${res.skipped_count} dòng không thể hoàn tác do đã bị sửa sau đó)`
            : ''
        }`,
      )
      await loadBatches()
      // Refresh scan if current tab matches
      if (activeTab === 'materials') await triggerScan('MATERIALS')
      if (activeTab === 'duplicate_specs') await triggerScan('DUPLICATE_SPECS')
      if (activeTab === 'standard_packaging') await triggerScan('STANDARD_PACKAGING')
      if (activeTab === 'structured_packaging') await triggerScan('STRUCTURED_PACKAGING')
    } catch (err: any) {
      alert(err.message || 'Lỗi khi hoàn tác lô')
    } finally {
      setRevertingBatchId(null)
    }
  }

  // View batch detail lines
  async function handleViewBatchLines(batch: DataChangeBatch) {
    setSelectedBatchForDetail(batch)
    setLoadingDetailLines(true)
    try {
      const lines = await dataStandardizationService.getBatchLines(batch.id)
      setDetailLines(lines)
    } catch (err: any) {
      alert(err.message || 'Không thể tải chi tiết dòng của lô')
    } finally {
      setLoadingDetailLines(false)
    }
  }

  // Excel template download
  function downloadHardwareTemplate() {
    const templateData = [
      {
        STT: 1,
        'Mã NCC': 'NCC-001',
        'Tên nhà cung cấp': 'CÔNG TY TNHH NGŨ KIM THẮNG LỢI',
        'Tên ngũ kim (chuẩn)': 'Bản lề giảm chấn thẳng',
        'Quy cách': 'Móng cong 0',
        'Màu sắc': 'Niken',
        'Đơn vị tính': 'Pcs',
        'Đơn giá (chưa VAT)': 4500,
        'Thuế VAT (%)': 8,
      },
      {
        STT: 2,
        'Mã NCC': 'NCC-001',
        'Tên nhà cung cấp': 'CÔNG TY TNHH NGŨ KIM THẮNG LỢI',
        'Tên ngũ kim (chuẩn)': 'Ray trượt 3 tầng bản 45',
        'Quy cách': 'L=450mm',
        'Màu sắc': 'Đen',
        'Đơn vị tính': 'Bộ',
        'Đơn giá (chưa VAT)': 28000,
        'Thuế VAT (%)': 8,
      },
      {
        STT: 3,
        'Mã NCC': 'NCC-002',
        'Tên nhà cung cấp': 'CÔNG TY CỔ PHẦN ỐC VÍT ĐẠI NAM',
        'Tên ngũ kim (chuẩn)': 'Vít bắt gỗ đầu bằng',
        'Quy cách': 'M4x20',
        'Màu sắc': 'Vàng 7 màu',
        'Đơn vị tính': 'Pcs',
        'Đơn giá (chưa VAT)': 120,
        'Thuế VAT (%)': 8,
      },
      {
        STT: 4,
        'Mã NCC': 'NCC-002',
        'Tên nhà cung cấp': 'CÔNG TY CỔ PHẦN ỐC VÍT ĐẠI NAM',
        'Tên ngũ kim (chuẩn)': 'Long đền phẳng',
        'Quy cách': 'M6',
        'Màu sắc': 'Trắng xanh',
        'Đơn vị tính': 'Pcs',
        'Đơn giá (chưa VAT)': 85,
        'Thuế VAT (%)': 8,
      },
    ]

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(templateData)
    XLSX.utils.book_append_sheet(wb, ws, 'Mau_Nhap_Ngu_Kim')
    XLSX.writeFile(wb, 'Mau_Nhap_Danh_Muc_Ngu_Kim_MocPhat.xlsx')
  }

  // Handle Excel file upload
  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    setImportingFile(true)
    try {
      const data = await file.arrayBuffer()
      const wb = XLSX.read(data, { type: 'array' })
      const firstSheetName = wb.SheetNames[0]
      const ws = wb.Sheets[firstSheetName]
      const rawRows: any[] = XLSX.utils.sheet_to_json(ws)

      if (rawRows.length === 0) {
        alert('File Excel không có dữ liệu!')
        return
      }

      // Map raw rows to HardwareImportRow
      const importRows: HardwareImportRow[] = rawRows.map((r, idx) => ({
        stt: idx + 1,
        supplier_code: String(r['Mã NCC'] || r['supplier_code'] || '').trim(),
        supplier_name: String(r['Tên nhà cung cấp'] || r['supplier_name'] || '').trim(),
        item_name: String(r['Tên ngũ kim (chuẩn)'] || r['Tên ngũ kim'] || r['item_name'] || '').trim(),
        spec: String(r['Quy cách'] || r['spec'] || '').trim() || undefined,
        color: String(r['Màu sắc'] || r['Màu'] || r['color'] || '').trim() || undefined,
        uom: String(r['Đơn vị tính'] || r['ĐVT'] || r['uom'] || 'Pcs').trim(),
        unit_price: Number(r['Đơn giá (chưa VAT)'] || r['Đơn giá'] || r['unit_price'] || 0),
        vat_rate: r['Thuế VAT (%)'] !== undefined ? Number(r['Thuế VAT (%)']) : 8,
      }))

      const previewRes = await dataStandardizationService.previewHardwareImport(importRows)
      setImportPreview(previewRes)
    } catch (err: any) {
      alert(err.message || 'Lỗi khi đọc file Excel')
    } finally {
      setImportingFile(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  // Apply Hardware Import
  async function handleApplyHardwareImport() {
    if (!importPreview || importPreview.valid_rows === 0) return
    setApplyingImport(true)
    try {
      const validItems = importPreview.items.filter((item) => item.valid)
      const res = await dataStandardizationService.applyHardwareImport({
        batch_name: `Nạp ngũ kim (${validItems.length} dòng - ${formatDateFull(new Date())})`,
        rows: validItems,
      })

      setActionSuccessMessage(
        `Nạp thành công lô '${res.batch.batch_name}': Tạo ${res.created_suppliers} NCC, ${res.created_materials} vật tư NK, ${res.created_quotes} báo giá, ${res.created_price_rules} quy tắc giá.`,
      )
      setImportPreview(null)
      await loadBatches()
      setActiveTab('history')
    } catch (err: any) {
      alert(err.message || 'Lỗi khi nạp danh mục ngũ kim')
    } finally {
      setApplyingImport(false)
    }
  }

  function getConfidenceBadge(confidence: ConfidenceLevel) {
    if (confidence === 'CONFIDENT') {
      return <StatusBadge tone="success" label="Chắc chắn" />
    }
    if (confidence === 'NEEDS_REVIEW') {
      return <StatusBadge tone="warning" label="Cần xem" />
    }
    return <StatusBadge tone="danger" label="Không rõ" />
  }

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mua hàng', to: '/purchasing' },
          { label: 'Chuẩn hóa dữ liệu' },
        ]}
      />

      <PageHeader
        title="Trung tâm Chuẩn hóa Dữ liệu"
        subtitle="Quét, xem trước tác động, duyệt áp dụng an toàn và hoàn tác theo lô trên cơ sở dữ liệu"
        actions={
          <div className="flex items-center gap-2">
            {activeTab === 'materials' || activeTab === 'duplicate_specs' || activeTab === 'standard_packaging' || activeTab === 'structured_packaging' ? (
              <>
                <Button
                  variant="secondary"
                  onClick={exportProposalsExcel}
                  disabled={filteredProposals.length === 0}
                  className="flex items-center gap-1.5"
                >
                  <Download className="h-4 w-4" />
                  Xuất Excel đề xuất
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setIsApplyModalOpen(true)}
                  disabled={selectedList.length === 0 || !canWrite}
                  className="flex items-center gap-1.5"
                >
                  <Check className="h-4 w-4" />
                  Duyệt và áp dụng ({selectedList.length})
                </Button>
              </>
            ) : null}
          </div>
        }
      />

      {/* Success Notification */}
      {actionSuccessMessage && (
        <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
            <span>{actionSuccessMessage}</span>
          </div>
          <button
            onClick={() => setActionSuccessMessage(null)}
            className="text-emerald-600 hover:text-emerald-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="border-b border-slate-200">
        <nav className="-mb-px flex space-x-6">
          <button
            onClick={() => setActiveTab('materials')}
            className={`flex items-center gap-2 border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
              activeTab === 'materials'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
            }`}
          >
            <Layers className="h-4 w-4" />
            Danh mục vật tư
          </button>

          <button
            onClick={() => setActiveTab('import_hardware')}
            className={`flex items-center gap-2 border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
              activeTab === 'import_hardware'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
            }`}
          >
            <FileSpreadsheet className="h-4 w-4" />
            Nhập ngũ kim từ Excel
          </button>

          <button
            onClick={() => setActiveTab('duplicate_specs')}
            className={`flex items-center gap-2 border-b-2 py-3 px-1 text-sm font-medium transition-colors ${activeTab === 'duplicate_specs' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'}`}
          >
            <Layers className="h-4 w-4" />
            Gộp quy cách trùng
          </button>

          <button onClick={() => setActiveTab('standard_packaging')} className={`flex items-center gap-2 border-b-2 py-3 px-1 text-sm font-medium ${activeTab === 'standard_packaging' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:border-slate-300'}`}>
            <Layers className="h-4 w-4" /> Vật tư bao bì chuẩn
          </button>
          <button onClick={() => setActiveTab('structured_packaging')} className={`flex items-center gap-2 border-b-2 py-3 px-1 text-sm font-medium ${activeTab === 'structured_packaging' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:border-slate-300'}`}>
            <FileCheck className="h-4 w-4" /> Định mức bao bì
          </button>

          <Link
            to="/purchasing/boms/review-names"
            className="flex items-center gap-2 border-b-2 border-transparent py-3 px-1 text-sm font-medium text-slate-500 hover:border-slate-300 hover:text-slate-700"
          >
            <ExternalLink className="h-4 w-4" />
            Rà soát tên BOM
          </Link>

          <button
            onClick={() => setActiveTab('history')}
            className={`flex items-center gap-2 border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
              activeTab === 'history'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
            }`}
          >
            <History className="h-4 w-4" />
            Lịch sử & Hoàn tác
            {batches.length > 0 && (
              <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                {batches.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* TAB 1, 2, 3: QUÉT & ĐỀ XUẤT */}
      {(activeTab === 'materials' || activeTab === 'duplicate_specs' || activeTab === 'standard_packaging' || activeTab === 'structured_packaging') && (
        <div className="space-y-5">
          {/* Action bar & KPI Cards */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                onClick={() =>
                  triggerScan(
                    activeTab === 'materials'
                      ? 'MATERIALS'
                      : activeTab === 'duplicate_specs'
                        ? 'DUPLICATE_SPECS'
                        : activeTab === 'standard_packaging'
                          ? 'STANDARD_PACKAGING'
                          : 'STRUCTURED_PACKAGING',
                  )
                }
                disabled={loadingScan}
                className="flex items-center gap-1.5"
              >
                <RefreshCw className={`h-4 w-4 ${loadingScan ? 'animate-spin' : ''}`} />
                {loadingScan ? 'Đang quét...' : 'Quét lại'}
              </Button>
              <span className="text-xs text-slate-500">
                Mặc định tích sẵn nhóm "Chắc chắn". Nhóm "Cần xem" yêu cầu kiểm tra kỹ trước khi duyệt.
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSelectConfidentOnly}
                className="text-xs text-blue-600"
              >
                Chọn tất cả Chắc chắn
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSelectAll(false)}
                className="text-xs text-slate-600"
              >
                Bỏ chọn tất cả
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <KpiCard
                label="Tổng đề xuất"
                value={stats.total_proposals.toString()}
                hint="Đề xuất cần chuẩn hóa"
              />
              <KpiCard
                label="Chắc chắn"
                value={stats.confident_count.toString()}
                hint="Khớp từ điển / Quy tắc chuẩn"
              />
              <KpiCard
                label="Cần xem"
                value={stats.needs_review_count.toString()}
                hint="Nhiều lựa chọn / Khác biệt"
              />
              <KpiCard
                label="Không rõ"
                value={stats.unknown_count.toString()}
                hint="Chưa có trong từ điển / Lỗi"
              />
            </div>
          )}

          {/* Filter Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex flex-1 items-center gap-2 min-w-[240px]">
              <Search className="h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Tìm theo mã, tên, lý do, tác động..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full text-xs outline-none bg-transparent"
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500">Mức tin cậy:</span>
              <select
                value={confidenceFilter}
                onChange={(e) => setConfidenceFilter(e.target.value)}
                className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-700 outline-none"
              >
                <option value="">Tất cả ({proposals.length})</option>
                <option value="CONFIDENT">Chắc chắn ({stats?.confident_count || 0})</option>
                <option value="NEEDS_REVIEW">Cần xem ({stats?.needs_review_count || 0})</option>
                <option value="UNKNOWN">Không rõ ({stats?.unknown_count || 0})</option>
              </select>
            </div>
          </div>

          {/* Proposals Table */}
          {loadingScan ? (
            <PageLoading label="Đang quét và phân tích dữ liệu..." />
          ) : scanError ? (
            <PageError message={scanError} onRetry={() => triggerScan(activeTab === 'materials' ? 'MATERIALS' : activeTab === 'duplicate_specs' ? 'DUPLICATE_SPECS' : activeTab === 'standard_packaging' ? 'STANDARD_PACKAGING' : 'STRUCTURED_PACKAGING')} />
          ) : filteredProposals.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center">
              <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" />
              <p className="mt-2 text-sm font-medium text-slate-700">Dữ liệu hoàn toàn sạch và chuẩn hóa!</p>
              <p className="mt-1 text-xs text-slate-500">Không phát hiện đề xuất nào cần chuẩn hóa theo bộ quy tắc này.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-xs">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-slate-600">
                    <th className="py-2.5 px-3 font-semibold text-center w-12">STT</th>
                    <th className="py-2.5 px-2 text-center w-10">
                      <input
                        type="checkbox"
                        checked={filteredProposals.length > 0 && filteredProposals.every((p) => selectedIds.has(p.id))}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="rounded border-slate-300 text-blue-600"
                      />
                    </th>
                    <th className="py-2.5 px-3 font-semibold">Đối tượng</th>
                    <th className="py-2.5 px-3 font-semibold">Hành động</th>
                    <th className="py-2.5 px-3 font-semibold">Hiện tại</th>
                    <th className="py-2.5 px-3 font-semibold">Đề xuất chuẩn hóa</th>
                    <th className="py-2.5 px-3 font-semibold">Mức tin cậy</th>
                    <th className="py-2.5 px-3 font-semibold">Lý do & Tác động</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredProposals.map((p, idx) => {
                    const isSelected = selectedIds.has(p.id)
                    return (
                      <tr
                        key={p.id}
                        className={`hover:bg-slate-50 transition-colors ${
                          isSelected ? 'bg-blue-50/40' : ''
                        }`}
                      >
                        <td className="py-2.5 px-3 text-center text-slate-500 font-mono text-[11px]">
                          {idx + 1}
                        </td>
                        <td className="py-2.5 px-2 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectRow(p.id)}
                            className="rounded border-slate-300 text-blue-600"
                          />
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="font-semibold text-slate-800">{p.entity_code}</div>
                          <div className="text-[11px] text-slate-500 truncate max-w-xs">{p.entity_name}</div>
                        </td>
                        <td className="py-2.5 px-3">
                          <span className={`inline-flex items-center rounded px-2 py-0.5 text-[10px] font-medium ${
                            p.action === 'delete'
                              ? 'bg-rose-100 text-rose-700'
                              : p.action === 'archive' || p.action === 'deactivate'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-blue-100 text-blue-700'
                          }`}>
                            {p.action === 'delete' ? 'XÓA' : p.action === 'archive' || p.action === 'deactivate' ? 'NGỪNG DÙNG' : p.action === 'update' ? 'CẬP NHẬT' : p.action.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="text-slate-600 font-mono text-[11px] max-w-xs break-words">
                            {p.current_display}
                          </div>
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="text-emerald-700 font-mono text-[11px] font-medium max-w-xs break-words">
                            {p.proposed_display}
                          </div>
                        </td>
                        <td className="py-2.5 px-3">
                          {getConfidenceBadge(p.confidence)}
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="text-slate-700 text-[11px]">{p.reason}</div>
                          {p.impact && (
                            <div className="text-[10px] text-slate-500 mt-0.5 italic">{p.impact}</div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TAB 4: NHẬP DANH MỤC NGŨ KIM TỪ EXCEL */}
      {activeTab === 'import_hardware' && (
        <div className="space-y-6">
          {/* Guidelines & Template */}
          <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-4">
            <h4 className="text-sm font-semibold text-blue-900 mb-1">
              Quy trình Nạp danh mục ngũ kim đồng bộ (Nhà cung cấp + Vật tư NK + Báo giá + Quy tắc giá)
            </h4>
            <p className="text-xs text-blue-800 mb-3">
              Áp dụng an toàn cho máy chủ thật chưa có danh mục ngũ kim: Tải file mẫu Excel, điền thông tin, hệ thống tự động kiểm tra từ điển tên chuẩn, sinh mã NK-xxxx tuần tự, gắn thuế VAT 8% và cho phép hoàn tác toàn bộ theo lô nếu phát hiện sai sót.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={downloadHardwareTemplate}
                className="flex items-center gap-1.5 bg-white text-xs"
              >
                <Download className="h-4 w-4" />
                Tải file mẫu Excel (.xlsx)
              </Button>

              <label className="flex items-center gap-1.5 cursor-pointer rounded-md border border-blue-600 bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 shadow-xs">
                <Upload className="h-4 w-4" />
                {importingFile ? 'Đang đọc file...' : 'Chọn file Excel nạp vào'}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={handleFileUpload}
                  disabled={importingFile}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Import Preview Result */}
          {importPreview && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-900">
                  Kết quả Xem trước kiểm tra dữ liệu ({importPreview.total_rows} dòng)
                </h4>
                <div className="flex items-center gap-2">
                  <Button
                    variant="primary"
                    disabled={importPreview.valid_rows === 0 || applyingImport || !canWrite}
                    onClick={handleApplyHardwareImport}
                    className="flex items-center gap-1.5"
                  >
                    <Check className="h-4 w-4" />
                    {applyingImport
                      ? 'Đang nạp vào hệ thống...'
                      : `Duyệt và nạp ${importPreview.valid_rows} dòng hợp lệ`}
                  </Button>
                </div>
              </div>

              {/* KPI Cards */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                <KpiCard
                  label="Tổng số dòng"
                  value={importPreview.total_rows.toString()}
                  hint="Từ file Excel"
                />
                <KpiCard
                  label="Hợp lệ"
                  value={importPreview.valid_rows.toString()}
                  hint="Sẵn sàng nạp"
                />
                <KpiCard
                  label="Không hợp lệ"
                  value={importPreview.invalid_rows.toString()}
                  hint="Tên ngoài từ điển / Thiếu giá"
                />
                <KpiCard
                  label="NCC Mới"
                  value={importPreview.new_suppliers_count.toString()}
                  hint="Sẽ tự động tạo mới"
                />
                <KpiCard
                  label="Vật tư mới"
                  value={importPreview.new_materials_count.toString()}
                  hint="Sinh mã NK-xxxx"
                />
              </div>

              {/* Preview Table */}
              <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-xs">
                <table className="w-full border-collapse text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-slate-600">
                      <th className="py-2.5 px-3 font-semibold text-center w-12">STT</th>
                      <th className="py-2.5 px-3 font-semibold">Trạng thái</th>
                      <th className="py-2.5 px-3 font-semibold">Nhà cung cấp</th>
                      <th className="py-2.5 px-3 font-semibold">Tên ngũ kim (Chuẩn)</th>
                      <th className="py-2.5 px-3 font-semibold">Quy cách</th>
                      <th className="py-2.5 px-3 font-semibold">Màu</th>
                      <th className="py-2.5 px-3 font-semibold">ĐVT</th>
                      <th className="py-2.5 px-3 font-semibold">Mã VT dự kiến</th>
                      <th className="py-2.5 px-3 font-semibold text-right">Đơn giá</th>
                      <th className="py-2.5 px-3 font-semibold text-center">VAT</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {importPreview.items.map((item, idx) => (
                      <tr
                        key={idx}
                        className={`hover:bg-slate-50 transition-colors ${
                          !item.valid ? 'bg-rose-50/50' : ''
                        }`}
                      >
                        <td className="py-2.5 px-3 text-center text-slate-500 font-mono text-[11px]">
                          {item.stt}
                        </td>
                        <td className="py-2.5 px-3">
                          {item.valid ? (
                            <StatusBadge tone="success" label="Hợp lệ" />
                          ) : (
                            <div>
                              <StatusBadge tone="danger" label="Lỗi" />
                              <div className="text-[10px] text-rose-600 mt-0.5">{item.error}</div>
                            </div>
                          )}
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="font-semibold text-slate-800">{item.supplier_code}</div>
                          <div className="text-[11px] text-slate-500 truncate max-w-xs">
                            {item.supplier_name}
                          </div>
                          <span className={`inline-block mt-0.5 px-1.5 py-0.2 rounded text-[9px] font-semibold ${
                            item.supplier_action === 'NEW'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {item.supplier_action === 'NEW' ? 'NCC MỚI' : 'NCC ĐÃ CÓ'}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 font-medium text-slate-800">
                          {item.item_name_standard}
                          {item.item_name_raw !== item.item_name_standard && (
                            <div className="text-[10px] text-slate-400">Gốc: {item.item_name_raw}</div>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-slate-600 font-mono text-[11px]">{item.spec || '-'}</td>
                        <td className="py-2.5 px-3 text-slate-600 text-[11px]">{item.color || '-'}</td>
                        <td className="py-2.5 px-3 text-slate-600 font-mono text-[11px]">{item.uom}</td>
                        <td className="py-2.5 px-3">
                          <span className="font-mono text-blue-700 font-medium">{item.material_code}</span>
                          <span className={`ml-1 text-[9px] px-1 py-0.2 rounded font-semibold ${
                            item.material_action === 'CREATE_NEW'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {item.material_action === 'CREATE_NEW' ? 'MỚI' : 'DÙNG LẠI'}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-slate-800">
                          {item.unit_price.toLocaleString('vi-VN')} đ
                        </td>
                        <td className="py-2.5 px-3 text-center font-mono text-slate-600">
                          {item.vat_rate}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 5: LỊCH SỬ & HOÀN TÁC */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-900">
              Nhật ký các Lô thay đổi dữ liệu (Tổng số: {batches.length})
            </h4>
            <Button
              variant="secondary"
              size="sm"
              onClick={loadBatches}
              disabled={loadingBatches}
              className="flex items-center gap-1.5"
            >
              <RefreshCw className={`h-4 w-4 ${loadingBatches ? 'animate-spin' : ''}`} />
              Làm mới lịch sử
            </Button>
          </div>

          {loadingBatches ? (
            <PageLoading label="Đang tải lịch sử các lô thay đổi..." />
          ) : batches.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center">
              <History className="mx-auto h-8 w-8 text-slate-400" />
              <p className="mt-2 text-sm font-medium text-slate-700">Chưa có lô thay đổi nào được thực hiện.</p>
              <p className="mt-1 text-xs text-slate-500">Mọi thao tác áp dụng từ các bộ quét sẽ được lưu tại đây để có thể hoàn tác an toàn.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-xs">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-slate-600">
                    <th className="py-2.5 px-3 font-semibold text-center w-12">STT</th>
                    <th className="py-2.5 px-3 font-semibold">Tên lô & Loại</th>
                    <th className="py-2.5 px-3 font-semibold">Trạng thái</th>
                    <th className="py-2.5 px-3 font-semibold text-center">Số dòng</th>
                    <th className="py-2.5 px-3 font-semibold">Người tạo & Thời điểm</th>
                    <th className="py-2.5 px-3 font-semibold">Ghi chú</th>
                    <th className="py-2.5 px-3 font-semibold text-center w-36">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {batches.map((b, idx) => {
                    const isReverted = b.status === 'reverted'
                    const isPartial = b.status === 'partial_reverted'
                    const isReverting = revertingBatchId === b.id

                    return (
                      <tr key={b.id} className="hover:bg-slate-50 transition-colors">
                        <td className="py-2.5 px-3 text-center text-slate-500 font-mono text-[11px]">
                          {idx + 1}
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="font-semibold text-slate-800">{b.batch_name}</div>
                          <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                            {b.id} · <span className="font-semibold text-blue-600">{b.scan_type}</span>
                          </div>
                        </td>
                        <td className="py-2.5 px-3">
                          {isReverted ? (
                            <StatusBadge tone="neutral" label="Đã hoàn tác" />
                          ) : isPartial ? (
                            <StatusBadge tone="warning" label="Hoàn tác một phần" />
                          ) : (
                            <StatusBadge tone="success" label="Đã áp dụng" />
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-center font-mono">
                          <span className="font-semibold text-emerald-700">{b.applied_lines}</span>
                          {b.reverted_lines > 0 && (
                            <span className="text-slate-400"> (Đã hủy {b.reverted_lines})</span>
                          )}
                          <span className="text-slate-400"> / {b.total_lines}</span>
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="font-medium text-slate-700">{b.created_by}</div>
                          <div className="text-[10px] text-slate-400">
                            {new Date(b.created_at).toLocaleString('vi-VN')}
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-slate-500 text-[11px]">
                          {b.notes || '-'}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              onClick={() => handleViewBatchLines(b)}
                              className="rounded border border-slate-300 bg-white p-1 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                              title="Xem chi tiết các dòng trong lô"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>

                            {!isReverted && canWrite && (
                              <button
                                onClick={() => setBatchToUndo(b)}
                                disabled={isReverting}
                                className="rounded border border-rose-300 bg-rose-50 px-2 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-100 flex items-center gap-1"
                                title="Hoàn tác an toàn lô này"
                              >
                                <RotateCcw className="h-3 w-3" />
                                {isReverting ? 'Đang hủy...' : 'Hoàn tác'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* MODAL 1: XÁC NHẬN ÁP DỤNG LÔ */}
      {isApplyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
          <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl">
            <h3 className="mb-2 text-base font-semibold text-slate-900">
              Xác nhận duyệt và áp dụng lô chuẩn hóa dữ liệu
            </h3>
            <p className="mb-4 text-xs text-slate-500">
              Bạn đang chuẩn bị áp dụng{' '}
              <strong className="text-blue-700">{selectedList.length} đề xuất chuẩn hóa</strong> vào cơ sở dữ liệu. Mọi thay đổi được thực hiện trong một giao dịch duy nhất (atomic transaction) và có thể hoàn tác bất cứ lúc nào.
            </p>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block mb-1 font-medium text-slate-700">Tên lô thay đổi</label>
                <input
                  type="text"
                  value={applyBatchName}
                  onChange={(e) => setApplyBatchName(e.target.value)}
                  placeholder={`Ví dụ: Chuẩn hóa ${activeTab} (${selectedList.length} dòng)`}
                  className="w-full rounded border border-slate-300 px-2.5 py-1.5 outline-none focus:border-blue-500 text-xs"
                />
              </div>

              <div>
                <label className="block mb-1 font-medium text-slate-700">Ghi chú (tùy chọn)</label>
                <textarea
                  value={applyNotes}
                  onChange={(e) => setApplyNotes(e.target.value)}
                  rows={2}
                  placeholder="Lý do hoặc ghi chú nội bộ..."
                  className="w-full rounded border border-slate-300 px-2.5 py-1.5 outline-none focus:border-blue-500 text-xs"
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setIsApplyModalOpen(false)}>
                Hủy
              </Button>
              <Button variant="primary" disabled={applying} onClick={handleConfirmApply}>
                {applying ? 'Đang thực hiện...' : 'Xác nhận áp dụng'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: XÁC NHẬN HOÀN TÁC LÔ */}
      {batchToUndo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <div className="flex items-center gap-2 text-rose-600 mb-2 font-semibold">
              <AlertTriangle className="h-5 w-5" />
              <span>Xác nhận hoàn tác lô thay đổi</span>
            </div>
            <p className="mb-4 text-xs text-slate-600">
              Bạn có chắc chắn muốn hoàn tác lô{' '}
              <strong className="text-slate-900">'{batchToUndo.batch_name}'</strong>?
              Dữ liệu sẽ được khôi phục về trạng thái trước khi áp dụng. Nếu có dòng nào đã bị sửa đổi tiếp sau đó, hệ thống sẽ bỏ qua dòng đó và thông báo rõ.
            </p>

            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setBatchToUndo(null)}>
                Hủy
              </Button>
              <Button
                variant="danger"
                disabled={revertingBatchId === batchToUndo.id}
                onClick={handleConfirmUndo}
              >
                {revertingBatchId === batchToUndo.id ? 'Đang hoàn tác...' : 'Đồng ý hoàn tác'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 3: XEM CHI TIẾT CÁC DÒNG TRONG LÔ */}
      {selectedBatchForDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
          <div className="w-full max-w-4xl max-h-[85vh] flex flex-col rounded-lg bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between pb-3 border-b border-slate-200">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">
                  Chi tiết các dòng trong Lô: {selectedBatchForDetail.batch_name}
                </h3>
                <p className="text-xs text-slate-500 font-mono">
                  ID: {selectedBatchForDetail.id} · Loại: {selectedBatchForDetail.scan_type}
                </p>
              </div>
              <button
                onClick={() => setSelectedBatchForDetail(null)}
                className="rounded p-1 text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-3">
              {loadingDetailLines ? (
                <PageLoading label="Đang tải danh sách các dòng..." />
              ) : detailLines.length === 0 ? (
                <p className="text-center text-xs text-slate-500 py-6">Lô này không có dòng thay đổi nào.</p>
              ) : (
                <table className="w-full border-collapse text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-slate-600">
                      <th className="py-2 px-3 font-semibold text-center w-12">STT</th>
                      <th className="py-2 px-3 font-semibold">Đối tượng</th>
                      <th className="py-2 px-3 font-semibold">ID</th>
                      <th className="py-2 px-3 font-semibold">Hành động</th>
                      <th className="py-2 px-3 font-semibold">Trường</th>
                      <th className="py-2 px-3 font-semibold">Giá trị cũ</th>
                      <th className="py-2 px-3 font-semibold">Giá trị mới</th>
                      <th className="py-2 px-3 font-semibold text-center">Hoàn tác</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {detailLines.map((line, idx) => (
                      <tr key={line.id} className="hover:bg-slate-50">
                        <td className="py-2 px-3 text-center text-slate-500 font-mono text-[11px]">{idx + 1}</td>
                        <td className="py-2 px-3 font-semibold text-slate-800">{line.entity_type}</td>
                        <td className="py-2 px-3 font-mono text-[11px] text-slate-600 truncate max-w-[120px]">{line.entity_id}</td>
                        <td className="py-2 px-3 font-mono text-blue-700">{line.action}</td>
                        <td className="py-2 px-3 font-mono text-slate-700">{line.field}</td>
                        <td className="py-2 px-3 font-mono text-[11px] text-slate-500 max-w-[150px] truncate">
                          {JSON.stringify(line.old_value)}
                        </td>
                        <td className="py-2 px-3 font-mono text-[11px] text-emerald-700 max-w-[150px] truncate">
                          {JSON.stringify(line.new_value)}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {line.reverted ? (
                            <span className="text-[10px] text-emerald-600 font-medium">Đã hủy</span>
                          ) : (
                            <span className="text-[10px] text-slate-400">Chưa</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="pt-3 border-t border-slate-200 flex justify-end">
              <Button variant="secondary" onClick={() => setSelectedBatchForDetail(null)}>
                Đóng
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
